import { FC, useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { trailsService } from '@/services/trails.service';
import LoadingSpinner from '@/components/LoadingSpinner';
import { useToast } from '@/contexts/ToastContext';
import type { LearnerLessonQuiz, QuizSubmitResult } from '@/types/trailDetail';

const QuizLessonPage: FC = () => {
  const { trailId, lessonId } = useParams<{ trailId: string; lessonId: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [quiz, setQuiz] = useState<LearnerLessonQuiz | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedAnswers, setSelectedAnswers] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState<QuizSubmitResult | null>(null);
  const [isCompleting, setIsCompleting] = useState(false);

  useEffect(() => {
    if (!trailId || !lessonId) return;
    const load = async () => {
      setIsLoading(true);
      setError(null);
      setResult(null);
      setSelectedAnswers({});
      try {
        const quizData = await trailsService.getLessonQuiz(trailId, lessonId);
        setQuiz(quizData);
      } catch {
        setError('Erro ao carregar o quiz');
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [trailId, lessonId]);

  const handleSelectAnswer = (questionId: string, answerId: string) => {
    if (result) return; // Don't allow changes after submit
    setSelectedAnswers((prev) => ({ ...prev, [questionId]: answerId }));
  };

  const allAnswered = quiz ? quiz.questions.every((q) => selectedAnswers[q.id]) : false;

  const handleSubmit = async () => {
    if (!trailId || !lessonId || !allAnswered) return;
    setIsSubmitting(true);
    try {
      const submitResult = await trailsService.submitLessonQuiz(trailId, lessonId, selectedAnswers);
      setResult(submitResult);
    } catch {
      showToast('Erro ao enviar quiz. Tente novamente.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRetry = async () => {
    setResult(null);
    setSelectedAnswers({});
    // Reload quiz to get new randomized order
    if (!trailId || !lessonId) return;
    setIsLoading(true);
    try {
      const quizData = await trailsService.getLessonQuiz(trailId, lessonId);
      setQuiz(quizData);
    } catch {
      setError('Erro ao recarregar quiz');
    } finally {
      setIsLoading(false);
    }
  };

  const handleContinue = async () => {
    if (!trailId || !lessonId || !result?.passed) return;
    setIsCompleting(true);
    try {
      // FSM: quiz submit already completed the lesson on the backend.
      // Use completion data from the quiz result if available.
      const completion = result.completion;

      if (completion) {
        // Backend already completed — navigate based on action
        switch (completion.action) {
          case 'next_lesson':
            if (completion.next_lesson_id) {
              navigate(`/trilhas/${trailId}/aula/${completion.next_lesson_id}`);
            } else {
              navigate(`/trilhas/${trailId}`);
            }
            break;
          case 'trail_completed':
            navigate(`/certificado/${trailId}`);
            break;
          default:
            navigate(`/trilhas/${trailId}`);
        }
      } else {
        // Fallback: fetch lesson content for navigation info
        const lessonContent = await trailsService.getLessonContent(trailId, lessonId);
        if (lessonContent.navigation.next) {
          navigate(`/trilhas/${trailId}/aula/${lessonContent.navigation.next.id}`);
        } else {
          navigate(`/trilhas/${trailId}`);
        }
      }

      window.dispatchEvent(new CustomEvent('notification:refresh'));
    } catch {
      // NEVER navigate silently — show error toast
      showToast('Erro ao avançar. Tente novamente.', 'error');
    } finally {
      setIsCompleting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-surface-primary">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (error || !quiz) {
    return (
      <div className="flex h-screen items-center justify-center bg-surface-primary">
        <div className="text-center">
          <p className="text-red-400 text-lg font-bold">Erro ao carregar quiz</p>
          <p className="text-content-muted mt-2">{error}</p>
          <Link
            to={`/trilhas/${trailId}/aula/${lessonId}`}
            className="text-accent mt-4 inline-block hover:underline"
          >
            Voltar para a aula
          </Link>
        </div>
      </div>
    );
  }

  const getResultForQuestion = (questionId: string) =>
    result?.results.find((r) => r.question_id === questionId);

  return (
    <div className="flex flex-col min-h-screen bg-surface-primary" data-testid="quiz-lesson-page">
      {/* Header */}
      <div className="bg-surface-card border-b border-theme-border px-6 py-4">
        <div className="max-w-3xl mx-auto flex items-center gap-4">
          <Link
            to={`/trilhas/${trailId}/aula/${lessonId}`}
            className="text-content-muted hover:text-content-primary shrink-0"
          >
            <span className="material-symbols-outlined">arrow_back</span>
          </Link>
          <div>
            <h1 className="text-lg font-bold">{quiz.title || 'Quiz'}</h1>
            <p className="text-sm text-content-muted">
              {quiz.questions.length} {quiz.questions.length === 1 ? 'pergunta' : 'perguntas'}
              {' — '}Mínimo de {quiz.min_correct_answers} {quiz.min_correct_answers === 1 ? 'acerto' : 'acertos'}
            </p>
          </div>
        </div>
      </div>

      {/* Result banner */}
      {result && (
        <div
          className={`px-6 py-4 text-center font-bold ${
            result.passed
              ? 'bg-green-500/10 text-green-400 border-b border-green-500/20'
              : 'bg-red-500/10 text-red-400 border-b border-red-500/20'
          }`}
        >
          {result.passed ? (
            <span>
              Parabéns! Você acertou {result.score} de {result.total}!
            </span>
          ) : (
            <span>
              Você acertou {result.score} de {result.total}. Mínimo necessário: {result.min_required}.
            </span>
          )}
        </div>
      )}

      {/* Questions */}
      <main className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto px-6 py-8 space-y-6">
          {quiz.questions.map((question, qIdx) => {
            const questionResult = getResultForQuestion(question.id);

            return (
              <div
                key={question.id}
                className="bg-surface-card border border-theme-border rounded-xl p-5"
              >
                <p className="font-medium mb-4">
                  <span className="text-accent font-bold mr-2">{qIdx + 1}.</span>
                  {question.text}
                </p>

                <div className="space-y-2">
                  {question.answers.map((answer) => {
                    const isSelected = selectedAnswers[question.id] === answer.id;
                    const showResult = !!questionResult;
                    const isCorrectAnswer = questionResult?.correct_answer_id === answer.id;

                    let borderClass = 'border-theme-border';
                    if (showResult) {
                      if (isCorrectAnswer) {
                        borderClass = 'border-green-500 bg-green-500/10';
                      } else if (isSelected && !questionResult?.correct) {
                        borderClass = 'border-red-500 bg-red-500/10';
                      }
                    } else if (isSelected) {
                      borderClass = 'border-accent bg-accent/10';
                    }

                    return (
                      <button
                        key={answer.id}
                        onClick={() => handleSelectAnswer(question.id, answer.id)}
                        disabled={!!result}
                        className={`w-full text-left flex items-center gap-3 px-4 py-3 rounded-lg border transition-all text-sm ${borderClass} ${
                          !result ? 'hover:border-accent/40 cursor-pointer' : 'cursor-default'
                        }`}
                      >
                        <span
                          className={`shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                            isSelected
                              ? showResult
                                ? isCorrectAnswer
                                  ? 'border-green-500 bg-green-500'
                                  : 'border-red-500 bg-red-500'
                                : 'border-accent bg-accent'
                              : showResult && isCorrectAnswer
                              ? 'border-green-500 bg-green-500'
                              : 'border-gray-500'
                          }`}
                        >
                          {(isSelected || (showResult && isCorrectAnswer)) && (
                            <span className="material-symbols-outlined text-white text-xs">
                              {showResult && isCorrectAnswer ? 'check' : isSelected ? 'circle' : ''}
                            </span>
                          )}
                        </span>
                        <span className="flex-1">{answer.text}</span>
                      </button>
                    );
                  })}
                </div>

                {/* Explanation */}
                {questionResult && questionResult.explanation && (
                  <div className="mt-3 px-4 py-2 bg-surface-tertiary rounded-lg text-sm text-content-secondary">
                    <span className="font-medium text-accent">Explicação: </span>
                    {questionResult.explanation}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </main>

      {/* Bottom bar */}
      <div className="border-t border-theme-border bg-surface-card px-6 py-4">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <Link
            to={`/trilhas/${trailId}/aula/${lessonId}`}
            className="flex items-center gap-2 text-content-muted hover:text-content-primary transition-colors"
          >
            <span className="material-symbols-outlined">arrow_back</span>
            <span className="text-sm font-medium">Voltar para a aula</span>
          </Link>

          {!result ? (
            <button
              onClick={handleSubmit}
              disabled={!allAnswered || isSubmitting}
              className="flex items-center gap-2 bg-accent hover:bg-accent-hover text-accent-contrast px-6 py-2.5 rounded-xl font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Enviando...' : 'Enviar Respostas'}
            </button>
          ) : result.passed ? (
            <button
              onClick={handleContinue}
              disabled={isCompleting}
              className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-2.5 rounded-xl font-medium transition-colors disabled:opacity-50"
            >
              {isCompleting ? 'Salvando...' : (
                <>
                  <span className="text-sm">Continuar</span>
                  <span className="material-symbols-outlined">arrow_forward</span>
                </>
              )}
            </button>
          ) : (
            <button
              onClick={handleRetry}
              className="flex items-center gap-2 bg-accent hover:bg-accent-hover text-accent-contrast px-6 py-2.5 rounded-xl font-medium transition-colors"
            >
              <span className="material-symbols-outlined">refresh</span>
              <span className="text-sm">Tentar Novamente</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuizLessonPage;
